import { EventEmitterInterface } from 'pf-frontend-common/src/module/event/emitter.interface';
import { createRef } from 'preact';

import { CountryCodeEnum } from 'common/data/country/code.enum';
import { UserLoginModel } from 'common/data/user/login/model';
import { configGetCountry } from 'common/helper/config/get-country';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { DataStore } from 'common/module/data/store';
import { GoogleRecaptchaViewStore } from 'common/module/google/recaptcha/view-store';
import { GoogleRecaptchaViewStoreEvent } from 'common/module/google/recaptcha/view-store.event';
import { InputValidatorInterface } from 'common/module/input/validator.interface';
import { InputValidatorEmail } from 'common/module/input/validator/email';
import { InputValidatorRequired } from 'common/module/input/validator/required';
import { KeyboardCharcodeEnum } from 'common/module/keyboard/charcode.enum';
import { StatechartStoreInterface } from 'common/module/statechart/store.interface';
import { StatsTealiumDataLayerEventReferenceEnum } from 'common/module/stats/tealium/data-layer/event-reference.enum';
import { StatsTealiumProviderInterface } from 'common/module/stats/tealium/provider.interface';
import { UserAuthenticationEvent } from 'common/module/user/authentication.event';
import { UserSignInFieldErrorsInterface } from 'common/module/user/sign-in/field-errors.interface';
import { UserSignInStatechart } from 'common/module/user/sign-in/statechart';
import { UserSignInStatechartEvent } from 'common/module/user/sign-in/statechart.event';
import { UserSignInStatechartActionEnum } from 'common/module/user/sign-in/statechart/action.enum';
import { UserSignInTemplatePropsInterface } from 'common/module/user/sign-in/template-props.interface';
import { UserSignInViewStoreEvent } from 'common/module/user/sign-in/view-store.event';
import { UserViewStoreInterface } from 'common/module/user/view-store.interface';
import { UserAuthenticationServiceInterface } from 'common/service/user-authentication/service.interface';

export class UserSignInViewStore extends DataStore<UserSignInTemplatePropsInterface> implements UserViewStoreInterface {
  /**
   * Constructor
   */
  constructor(
    eventEmitter: EventEmitterInterface,
    private userAuthenticationService: UserAuthenticationServiceInterface,
    private captcha: GoogleRecaptchaViewStore,
    private statechartStore: StatechartStoreInterface,
    private statsTealium: StatsTealiumProviderInterface
  ) {
    super(eventEmitter);
  }

  /**
   * @inheritDoc
   */
  public initialize(): void {
    this.state = {
      error: '',
      fields: new UserLoginModel(),
      fieldErrors: {
        email: '',
        password: '',
        captcha_token: '',
      },
      googleButtonRef: createRef<HTMLDivElement>(),
      loaderEnabled: false,
      captcha: this.captcha.getState(),
      activePage: 'create',
      isLoginActive: false,
      isRegisterActive: false,
      isPasswordVisible: false,
      isAdditionalConditionEnabled: configGetCountry().code === CountryCodeEnum.ma,
      setActivePage: this.setActivePage,
      setIsLoginActive: this.setIsLoginActive,
      setIsRegisterActive: this.setIsRegisterActive,
      setIsPasswordVisible: this.setIsPasswordVisible,
      onClickSignInFacebook: this.onClickSignInFacebook,
      onClickSignInGoogle: this.onClickSignInGoogle,
      onClickForgotPassword: this.onClickForgotPassword,
      onClickRememberMe: this.onClickRememberMe,
      onClickCreateAccount: this.onClickCreateAccount,
      onClickLogin: this.onClickLogin,
      onClickTermsAndConditions: this.onClickTermsAndConditions,
      onClickUseEmail: this.onClickUseEmail,
      onKeyUpInput: this.onKeyUpInput,
      onChangeInput: this.onChangeInput,
      onClose: this.onClose,
      onBack: this.onBack,
    };

    this.statsTealium.setEventReference(StatsTealiumDataLayerEventReferenceEnum.HEADER);
    this.statechartStore.initialize({
      statechart: UserSignInStatechart,
    });

    // Attach observers
    this.observers();
  }

  /**
   * @inheritDoc
   */
  public updateLoaderEnabled(enabled: boolean): void {
    this.setState(
      {
        ...this.state,
        loaderEnabled: enabled,
      },
      this.updateLoaderEnabled
    );
  }

  /**
   * Reset state
   */
  public reset(): void {
    const userLoginFields = new UserLoginModel();
    userLoginFields.email = this.state.fields.email;
    userLoginFields.remember_me = this.state.fields.remember_me;
    userLoginFields.captcha_token = this.state.fields.captcha_token;

    this.state = {
      ...this.state,
      error: '',
      fields: userLoginFields,
      fieldErrors: {
        email: '',
        password: '',
        captcha_token: '',
      },
    };

    this.setState(this.state);

    this.statechartStore.transit({
      event: UserSignInStatechartEvent.reset,
      payload: null,
    });
  }

  /**
   * sets tealium data layer event reference for user sign in component
   * @param eventReference
   */
  public setEventReference(eventReference: StatsTealiumDataLayerEventReferenceEnum): void {
    this.statsTealium.setEventReference(eventReference);
  }

  /**
   * Form validators
   */
  protected getValidators(): {
    [key: string]: InputValidatorInterface[];
  } {
    return {
      email: [
        new InputValidatorRequired(i18nTranslate('Please enter your email')),
        new InputValidatorEmail(i18nTranslate('Please enter a valid email')),
      ],
      password: [new InputValidatorRequired(i18nTranslate('Please enter a password'))],
      captcha_token: [],
    };
  }

  /**
   * Attach observers
   */
  private observers(): void {
    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.signInFailed, this.onSignInFailed);

    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.signInValidationFailed, this.onSignInValidationFailed);

    this.captcha.getEventEmitter().addListener(GoogleRecaptchaViewStoreEvent.change, this.onChangeCaptcha);

    this.statechartStore
      .getEventEmitter()
      .addListener(UserSignInStatechartActionEnum.validateInput, this.onValidateInputActionStatechart);

    this.statechartStore
      .getEventEmitter()
      .addListener(UserSignInStatechartActionEnum.executeCaptcha, this.onExecuteCaptchaActionStatechart);

    this.statechartStore
      .getEventEmitter()
      .addListener(UserSignInStatechartActionEnum.setRememberMe, this.onSetRememberMeActionStatechart);

    this.statechartStore
      .getEventEmitter()
      .addListener(UserSignInStatechartActionEnum.showLoader, this.onShowLoaderActionStatechart);

    this.statechartStore
      .getEventEmitter()
      .addListener(UserSignInStatechartActionEnum.signIn, this.onSignInActionStatechart);

    this.statechartStore
      .getEventEmitter()
      .addListener(UserSignInStatechartActionEnum.resetCaptchaToken, this.onResetCaptchaTokenActionStatechart);
  }

  /**
   * Log user in
   */
  private login(): void {
    this.statechartStore.transit({
      event: UserSignInStatechartEvent.wantToSignIn,
      payload: null,
    });
  }

  /**
   * Validate form fields
   */
  private validateFields(): boolean {
    let isValid = true;

    const validators = this.getValidators();

    Object.keys(validators).forEach((fieldName: keyof UserSignInFieldErrorsInterface) => {
      const errors: string[] = validators[fieldName]
        .map((validator: InputValidatorInterface) => validator.getError(this.state.fields[fieldName], null))
        .filter((message: string) => !!message);

      // Only keep first error if any
      this.state.fieldErrors[fieldName] = errors[0] || '';
      isValid = isValid && !this.state.fieldErrors[fieldName];
    });

    this.setState(this.state);

    return isValid;
  }

  /**
   * Type in input
   */
  private onKeyUpInput = (e: KeyboardEvent): void => {
    if (e.keyCode === KeyboardCharcodeEnum.enter) {
      this.login();
      return;
    }
  };

  /**
   * Change input value
   */
  private onChangeInput = (e: Event, fieldName: keyof PickByValue<UserLoginModel, string>): void => {
    this.state.fields[fieldName] = (<HTMLInputElement>e.target).value;
    this.setState(this.state);
  };

  /**
   * Click close button
   */
  private onClose = (): void => {
    this.setState({
      ...this.state,
      activePage: 'create',
      isLoginActive: false,
      isRegisterActive: false,
      isPasswordVisible: false,
    });
    this.getEventEmitter().emit(UserSignInViewStoreEvent.clickClose);
  };

  /**
   * @inheritDoc
   */
  private onClickSignInFacebook = () => {
    if (
      this.state.isAdditionalConditionEnabled &&
      (!this.state.fields.useEmail || !this.state.fields.termsAndConditions)
    ) {
      return;
    }

    this.getEventEmitter().emit(UserSignInViewStoreEvent.clickSignInWithFacebook);

    this.userAuthenticationService.signInWithFacebook();
    this.updateLoaderEnabled(false);

    // Every time close modal to avoid infinite loader in modal popup
    this.getEventEmitter().emit(UserSignInViewStoreEvent.clickClose);
  };

  /**
   * @inheritDoc
   */
  private onClickSignInGoogle = () => {
    if (
      this.state.isAdditionalConditionEnabled &&
      (!this.state.fields.useEmail || !this.state.fields.termsAndConditions)
    ) {
      return;
    }

    this.getEventEmitter().emit(UserSignInViewStoreEvent.clickSignInWithGoogle);

    this.userAuthenticationService.signInWithGoogle();
    this.updateLoaderEnabled(false);

    // Every time close modal to avoid infinite loader in modal popup
    this.getEventEmitter().emit(UserSignInViewStoreEvent.clickClose);
  };

  /**
   * @inheritDoc
   */
  private onClickForgotPassword = () => {
    this.getEventEmitter().emit(UserSignInViewStoreEvent.clickForgotPassword);
  };

  /**
   * @inheritDoc
   */
  private onClickRememberMe = (isChecked: boolean) => {
    this.state.fields.remember_me = isChecked;
    this.setState(this.state);
  };

  /**
   * @inheritDoc
   */
  private onClickCreateAccount = () => {
    this.getEventEmitter().emit(UserSignInViewStoreEvent.clickCreateAccount);
  };

  /**
   * @inheritDoc
   */
  private onClickLogin = () => {
    this.login();
  };

  /**
   * @inheritDoc
   */
  private onClickTermsAndConditions = () => {
    this.state.fields.termsAndConditions = !this.state.fields.termsAndConditions;
    this.setState(this.state);
  };

  /**
   * @inheritDoc
   */
  private onClickUseEmail = () => {
    this.state.fields.useEmail = !this.state.fields.useEmail;
    this.setState(this.state);
  };

  /**
   * Captcha value has changed
   */
  private onChangeCaptcha = () => {
    this.state.fields.captcha_token = this.captcha.getState().value;

    this.setState(this.state);

    this.statechartStore.transit({
      event: UserSignInStatechartEvent.captchaValueChanged,
      payload: null,
    });
  };

  /**
   * Sign in failed
   */
  private onSignInFailed = (reason: string) => {
    this.state.error = reason;
    this.setState(this.state);

    this.statechartStore.transit({
      event: UserSignInStatechartEvent.signInFailed,
      payload: null,
    });
  };

  /**
   * Sign in validation failed
   */
  private onSignInValidationFailed = (errors: UserSignInFieldErrorsInterface) => {
    if (errors.captcha_token.length) {
      this.captcha.reset();
    }

    this.state.fieldErrors = errors;
    this.setState(this.state);

    this.statechartStore.transit({
      event: UserSignInStatechartEvent.signInFailed,
      payload: null,
    });
  };

  /**
   * Validate input
   */
  private onValidateInputActionStatechart = () => {
    const isValid = this.validateFields();

    if (isValid) {
      this.statechartStore.transit({
        event: UserSignInStatechartEvent.inputIsValid,
        payload: null,
      });

      return;
    }

    this.statechartStore.transit({
      event: UserSignInStatechartEvent.inputIsNotValid,
      payload: null,
    });
  };

  /**
   * Execute captcha
   */
  private onExecuteCaptchaActionStatechart = () => {
    if (this.state.fields.captcha_token) {
      this.statechartStore.transit({
        event: UserSignInStatechartEvent.captchaValueChanged,
        payload: null,
      });

      return;
    }

    this.captcha.execute();
  };

  /**
   * Set remember me flag
   */
  private onSetRememberMeActionStatechart = () => {
    if (!this.state.isAdditionalConditionEnabled) {
      return;
    }

    this.state.fields.remember_me = true;
  };

  /**
   * Register
   */
  private onSignInActionStatechart = () => {
    this.userAuthenticationService.signIn(this.state.fields);
  };

  /**
   * Show loader
   */
  private onShowLoaderActionStatechart = () => {
    this.updateLoaderEnabled(true);
  };

  /**
   * Reset captcha_token field
   */
  private onResetCaptchaTokenActionStatechart = () => {
    this.captcha.reset();
    this.state.fields.captcha_token = '';
    this.setState(this.state);
  };

  /**
   * Set active page
   */
  private setActivePage = (activePage: 'create' | 'login') => {
    this.setState({
      ...this.state,
      activePage,
      isLoginActive: false,
      isRegisterActive: false,
    });
    this.reset();
  };

  /**
   * Set is login active
   */
  private setIsLoginActive = (isActive: boolean) => {
    this.setState({
      ...this.state,
      isLoginActive: isActive,
    });
  };

  /**
   * Set is register active
   */
  private setIsRegisterActive = (isActive: boolean) => {
    this.setState({
      ...this.state,
      isRegisterActive: isActive,
    });
  };

  /**
   * Set is password visible
   */
  private setIsPasswordVisible = (isVisible: boolean) => {
    this.setState({
      ...this.state,
      isPasswordVisible: isVisible,
    });
  };

  /**
   * Click back button
   */
  private onBack = (): void => {
    this.setState({
      ...this.state,
      isLoginActive: false,
      isRegisterActive: false,
    });
  };
}
